import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Breadcrumbs,
    Card,
    Chip,
    CircularProgress,
    Tab, TabPanel, TabList, Tabs,
    Sheet
} from "@mui/joy";
import Box from "@mui/joy/Box";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import Typography from "@mui/joy/Typography";
import { toast, ToastContainer } from "react-toastify";
import Stripe from "../dashboard-components/Stripe";
import StripeLeadPaymentModal from "../modals/StripeLeadPaymentModal";
import Button from "@mui/joy/Button";
import {
    AdsClickOutlined, PaymentOutlined,
    FileDownload, SwapHoriz, CreditCard, Add, DeleteOutline,
    BoltOutlined, TuneOutlined, WarningAmberRounded, CheckCircle
} from "@mui/icons-material";
import Switch from "@mui/joy/Switch";
import StripeBalanceTopUpModal from "../modals/StripeBalanceTopUpModal";
import Tooltip from "@mui/joy/Tooltip";
import PastPaymentsTable from "../tables/PastPaymentsTable";
import LeadConversionsTable from "../tables/LeadConversionsTable";
import { tabClasses } from '@mui/joy/Tab';
import FormLabel from "@mui/joy/FormLabel";
import FormControl from "@mui/joy/FormControl";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Link from "@mui/joy/Link";

import GeneralApiService from "../../axios/Axios";

interface PaymentMethodItem { id: string; type?: string; brand: string; last4: string; email?: string; exp_month: number | null; exp_year: number | null; default: boolean }

interface Franchise {
    location: string;
    gutter_ads_status: string;
    roof_ads_status: string;
    stripe_account_linked: boolean;
    recurring_leads_purchase: boolean;
    lead_balance: number;
    payment_invoice_url: string;
    stripe_account_id: string;
    stripe_customer_account_id: string;
    auto_recharge_threshold?: number;
    auto_recharge_amount?: number;
    auto_recharge_failed_attempts?: number;
    auto_recharge_locked_until_paid?: boolean;
    auto_recharge_last_failure_reason?: string;
    auto_recharge_last_failed_invoice_url?: string;
    auto_recharge_monthly_cap_enabled?: boolean;
    auto_recharge_monthly_cap_amount?: number | null;
    current_month_topup_total?: number;
}

export default function LeadGeneration() {
    const [franchise, setFranchise] = useState<Franchise | null>(null);
    const [loadedData, setLoadingData] = useState(true);
    const [selectedAds, setSelectAds] = useState('residential_ads');
    const [selectedAdsToggle, setSelectAdsToggle] = useState<string>('');
    const [openStripeModal, setOpenStripeModal] = useState<boolean>(false);
    const [openBalanceTopUpModal, setOpenBalanceTopUpModal] = useState<boolean>(false);
    const [index, setIndex] = useState(0);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>([]);
    const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);
    const [autoRechargeThreshold, setAutoRechargeThreshold] = useState<number>(50);
    const [autoRechargeAmount, setAutoRechargeAmount] = useState<number>(300);
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
    const [savingAutoRecharge, setSavingAutoRecharge] = useState(false);
    const [removingPaymentMethodId, setRemovingPaymentMethodId] = useState<string | null>(null);
    const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
    const [monthlyCapEnabled, setMonthlyCapEnabled] = useState(false);
    const [monthlyCapAmount, setMonthlyCapAmount] = useState<number>(1000);
    const [monthlySpent, setMonthlySpent] = useState<number>(0);

    useEffect(() => {
        const loadInitialData = async () => {
            setLoadingData(true);
            try {
                const adsData = await GeneralApiService.getToggledAds();
                setFranchise(adsData);
            } catch (error) {
                console.error("Failed to load lead generation data:", error);
                toast.error("Could not load page data.");
            } finally {
                setLoadingData(false);
            }
        };
        loadInitialData();
    }, []);

    const handleCallback = (field: string, on: boolean, toggleOffReason?: string) => {
        setOpenStripeModal(false);
        setOpenBalanceTopUpModal(false);
        if (field) {
            const status = on ? 'Pending' : 'Off';
            GeneralApiService.setFranchiseAdStatus(field, status, toggleOffReason)
                .then(() => {
                    setFranchise(prev => {
                        if (!prev) return null;
                        if (field === 'roof_clean') return { ...prev, roof_ads_status: status };
                        if (field === 'gutter_clean') return { ...prev, gutter_ads_status: status };
                        return prev;
                    });
                })
                .catch(err => toast.error(`Failed to update ${field} ad status.`));
        }
    };

    useEffect(() => {
        if (franchise?.stripe_customer_account_id && index === 0) {
            setLoadingPaymentMethods(true);
            GeneralApiService.getStripePaymentMethods()
                .then((res: { payment_methods: PaymentMethodItem[]; default_payment_method: string | null }) => {
                    setPaymentMethods(res.payment_methods || []);
                    setDefaultPaymentMethodId(res.default_payment_method || null);
                    setSelectedPaymentMethodId(res.default_payment_method || (res.payment_methods?.[0]?.id) || null);
                })
                .catch(() => setPaymentMethods([]))
                .finally(() => setLoadingPaymentMethods(false));
        } else if (!franchise?.stripe_customer_account_id) {
            setLoadingPaymentMethods(false);
        }
    }, [franchise?.stripe_customer_account_id, index]);

    useEffect(() => {
        if (franchise) {
            setAutoRechargeThreshold(franchise.auto_recharge_threshold ?? 50);
            setAutoRechargeAmount(franchise.auto_recharge_amount ?? 300);
            setMonthlyCapEnabled(franchise.auto_recharge_monthly_cap_enabled ?? false);
            setMonthlyCapAmount(Number(franchise.auto_recharge_monthly_cap_amount) || 1000);
            setMonthlySpent(Number(franchise.current_month_topup_total) || 0);
        }
    }, [franchise]);

    const handleRemovePaymentMethod = (paymentMethodId: string) => {
        setRemovingPaymentMethodId(paymentMethodId);
        GeneralApiService.removePaymentMethod(paymentMethodId)
            .then(() => {
                toast.success("Card removed.");
                return GeneralApiService.getToggledAds();
            })
            .then((adsData) => {
                setFranchise(adsData);
                if (adsData?.stripe_customer_account_id) {
                    return GeneralApiService.getStripePaymentMethods().then((res: { payment_methods: PaymentMethodItem[]; default_payment_method: string | null }) => {
                        setPaymentMethods(res.payment_methods || []);
                        setDefaultPaymentMethodId(res.default_payment_method || null);
                        setSelectedPaymentMethodId(res.default_payment_method || (res.payment_methods?.[0]?.id) || null);
                    });
                }
                setPaymentMethods([]);
                setDefaultPaymentMethodId(null);
                setSelectedPaymentMethodId(null);
            })
            .catch((err) => toast.error(err.response?.data?.error || "Failed to remove card."))
            .finally(() => setRemovingPaymentMethodId(null));
    };

    const validateAndSaveAutoRecharge = (enable: boolean) => {
        if (enable && !selectedPaymentMethodId) {
            toast.error("Please select a payment method.");
            return;
        }
        const t = Number(autoRechargeThreshold);
        const a = Number(autoRechargeAmount);
        if (enable) {
            if (t < 10 || t > 1000) { toast.error("Threshold must be between £10 and £1,000."); return; }
            if (a < 100 || a > 5000) { toast.error("Amount must be between £100 and £5,000."); return; }
            if (a <= t) { toast.error("Bring balance back to must be greater than threshold."); return; }
            if (monthlyCapEnabled && monthlyCapAmount < a) {
                toast.error("Monthly cap must be at least as large as the top-up amount.");
                return;
            }
        }
        setSavingAutoRecharge(true);
        GeneralApiService.updateAutoRecharge(
            enable,
            enable ? t : undefined,
            enable ? a : undefined,
            enable ? selectedPaymentMethodId ?? undefined : undefined,
            enable ? monthlyCapEnabled : false,
            enable && monthlyCapEnabled ? monthlyCapAmount : undefined
        )
            .then(() => {
                toast.success(enable ? "Auto Recharge updated." : "Auto Recharge disabled.");
                GeneralApiService.getToggledAds().then(setFranchise);
            })
            .catch((err) => toast.error(err.response?.data?.error || "Failed to update Auto Recharge."))
            .finally(() => setSavingAutoRecharge(false));
    };

    const toggleAd = (field: 'roof_clean' | 'gutter_clean') => {
        if (!franchise) return;
        const adStatus = field === 'roof_clean' ? franchise.roof_ads_status : franchise.gutter_ads_status;
        if (adStatus === 'Pending') {
            if (franchise.payment_invoice_url) {
                window.location.replace(franchise.payment_invoice_url);
            } else {
                toast.warn('Your ads are being set up! If this takes longer than expected, our support team is here to help.', { theme: "dark" });
            }
            return;
        }
        setSelectAdsToggle(adStatus);
        setSelectAds(field);
        setOpenStripeModal(true);
    };

    const handleDownload = async () => {
        try {
            const blob = await GeneralApiService.downloadAdBalanceChangesCSV();
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `balance_changes_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (error) {
            console.error("Failed to download CSV:", error);
            toast.error("Failed to download CSV.");
        }
    };

    // Helper: percentage of monthly cap used
    const capPercent = monthlyCapAmount > 0 ? Math.min((monthlySpent / monthlyCapAmount) * 100, 100) : 0;
    const capReached = monthlyCapEnabled && monthlySpent >= monthlyCapAmount && monthlyCapAmount > 0;
    const failedAttempts = franchise?.auto_recharge_failed_attempts ?? 0;
    const autoRechargeLocked = franchise?.auto_recharge_locked_until_paid ?? false;
    const failureReason = franchise?.auto_recharge_last_failure_reason ?? '';
    const manualPaymentUrl = franchise?.auto_recharge_last_failed_invoice_url || franchise?.payment_invoice_url || '';
    const showAutoRechargeWarning = autoRechargeLocked || failedAttempts > 0 || !!failureReason || !!manualPaymentUrl;

    return (
        <>
            <StripeLeadPaymentModal selectedAdToggled={selectedAdsToggle} selectedAd={selectedAds}
                                    franchise={franchise} parentCallback={handleCallback} openState={openStripeModal} />
            <StripeBalanceTopUpModal franchise={franchise} parentCallback={handleCallback} openState={openBalanceTopUpModal} onSaved={() => { GeneralApiService.getToggledAds().then(setFranchise); }} />
            <ToastContainer />

            {/* Breadcrumbs */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Breadcrumbs size="sm" aria-label="breadcrumbs" separator={<ChevronRightRoundedIcon />} sx={{ pl: 0 }}>
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home"><HomeRoundedIcon /></Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>Dashboard</Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>Lead Generation</Typography>
                </Breadcrumbs>
            </Box>

            {loadedData || !franchise ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', flexDirection: 'column', gap: 2 }}>
                    <CircularProgress size="lg" />
                    <Typography level="body-sm" color="neutral">Loading your lead generation dashboard...</Typography>
                </Box>
            ) : (
                <>
                    {!franchise.stripe_account_linked ? (
                        <Stripe franchise={franchise} />
                    ) : (
                        <>
                            {/* Page header */}
                            <Box sx={{ mb: { xs: 2, md: 3 } }}>
                                <Typography level="h2" component="h1" sx={{ mb: 0.5, fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
                                    Lead Generation
                                </Typography>
                                <Typography level="body-md" color="neutral">
                                    Manage your advertising campaigns, payments and auto-recharge.
                                </Typography>
                            </Box>

                            {/* ── Balance Hero Card ── */}
                            <Box
                                sx={{
                                    mb: { xs: 2, md: 3 },
                                    p: { xs: 2.5, md: 3 },
                                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%)',
                                    borderRadius: 'var(--joy-radius-lg)',
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    justifyContent: 'space-between',
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                                        Marketing Balance
                                    </Typography>
                                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '2rem', md: '2.5rem' }, lineHeight: 1.2 }}>
                                        £{franchise.lead_balance.toFixed(2)}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', mt: 0.5 }}>
                                        Keep above £100 to ensure uninterrupted ads
                                    </Typography>
                                </Box>
                                <Button
                                    size="lg"
                                    onClick={() => setOpenBalanceTopUpModal(true)}
                                    startDecorator={<Add />}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        color: 'white',
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        fontWeight: 600,
                                        px: 3,
                                        flexShrink: 0,
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.25)',
                                        },
                                    }}
                                >
                                    Top Up
                                </Button>
                            </Box>

                            {/* ── Tabs ── */}
                            <Tabs
                                aria-label="Lead Generation Tabs"
                                value={index}
                                onChange={(event, value) => setIndex(value as number)}
                            >
                                <TabList
                                    variant="plain"
                                    sx={{
                                        borderRadius: 'lg',
                                        bgcolor: 'background.level1',
                                        p: 0.5,
                                        gap: 0.5,
                                        overflowX: 'auto',
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        scrollbarWidth: 'none',
                                        [`& .${tabClasses.root}`]: {
                                            flex: 'initial',
                                            borderRadius: 'md',
                                            flexShrink: 0,
                                            fontWeight: 500,
                                            fontSize: '0.85rem',
                                            py: 1,
                                            px: { xs: 1.5, sm: 2 },
                                            transition: 'all 0.15s',
                                            '&:hover': {
                                                bgcolor: 'background.level2',
                                            },
                                            [`&.${tabClasses.selected}`]: {
                                                bgcolor: 'background.surface',
                                                boxShadow: 'sm',
                                                color: 'primary.plainColor',
                                            },
                                        },
                                    }}
                                >
                                    <Tab>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <CreditCard sx={{ fontSize: 18 }} />
                                            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Payment & Auto Recharge</Box>
                                            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Payment</Box>
                                        </Box>
                                    </Tab>
                                    <Tab>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <AdsClickOutlined sx={{ fontSize: 18 }} />
                                            <span>Campaigns</span>
                                        </Box>
                                    </Tab>
                                    <Tab>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <PaymentOutlined sx={{ fontSize: 18 }} />
                                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Past Charges</Box>
                                            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Charges</Box>
                                        </Box>
                                    </Tab>
                                    <Tab>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <SwapHoriz sx={{ fontSize: 18 }} />
                                            <span>Conversions</span>
                                        </Box>
                                    </Tab>
                                </TabList>

                                {/* ────────────────────────────────────────────────
                                    TAB 0 — Payment & Auto Recharge
                                ──────────────────────────────────────────────── */}
                                <TabPanel value={0} sx={{ p: 0, pt: { xs: 2, md: 3 } }}>
                                    {loadingPaymentMethods ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, flexDirection: 'column', gap: 1.5 }}>
                                            <CircularProgress size="md" />
                                            <Typography level="body-sm" color="neutral">Loading payment methods...</Typography>
                                        </Box>
                                    ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>

                                        {/* ─── Section: Payment Methods ─── */}
                                        <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 'lg' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box sx={{
                                                        width: 40, height: 40, borderRadius: 'md',
                                                        bgcolor: 'primary.softBg',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <CreditCard sx={{ fontSize: 20, color: 'primary.plainColor' }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography level="title-lg" sx={{ lineHeight: 1.2 }}>Payment Methods</Typography>
                                                        <Typography level="body-xs" color="neutral">Saved cards for top-ups and auto-recharge</Typography>
                                                    </Box>
                                                </Box>
                                                <Button
                                                    size="sm"
                                                    variant="soft"
                                                    color="primary"
                                                    startDecorator={<Add />}
                                                    onClick={() => setOpenBalanceTopUpModal(true)}
                                                >
                                                    Add card
                                                </Button>
                                            </Box>

                                            {paymentMethods.length > 0 ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {paymentMethods.map((pm) => (
                                                        <Sheet
                                                            key={pm.id}
                                                            variant="outlined"
                                                            sx={{
                                                                p: { xs: 1.5, sm: 2 },
                                                                borderRadius: 'md',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: { xs: 1.5, sm: 2 },
                                                                transition: 'border-color 0.15s',
                                                                '&:hover': { borderColor: 'primary.outlinedBorder' },
                                                            }}
                                                        >
                                                            {/* Payment method icon */}
                                                            <Box sx={{
                                                                width: 44, height: 30, borderRadius: '6px',
                                                                bgcolor: pm.type === 'link' ? '#00d66f' : pm.brand === 'visa' ? '#1a1f71' : pm.brand === 'mastercard' ? '#eb001b' : pm.brand === 'amex' ? '#006fcf' : 'neutral.400',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                flexShrink: 0,
                                                            }}>
                                                                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.6rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                                                    {pm.type === 'link' ? 'LINK' : pm.brand === 'visa' ? 'VISA' : pm.brand === 'mastercard' ? 'MC' : pm.brand === 'amex' ? 'AMEX' : (pm.brand || '').slice(0, 4).toUpperCase()}
                                                                </Typography>
                                                            </Box>

                                                            {/* Payment method details */}
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography level="title-sm" noWrap>
                                                                        {pm.type === 'link' ? (pm.email || 'Link') : `•••• ${pm.last4}`}
                                                                    </Typography>
                                                                    {pm.default && (
                                                                        <Chip size="sm" variant="soft" color="primary" sx={{ '--Chip-minHeight': '20px', fontSize: '0.65rem' }}>
                                                                            Default
                                                                        </Chip>
                                                                    )}
                                                                </Box>
                                                                <Typography level="body-xs" color="neutral">
                                                                    {pm.type === 'link' ? 'Stripe Link' : `Expires ${String(pm.exp_month).padStart(2, '0')}/${pm.exp_year}`}
                                                                </Typography>
                                                            </Box>

                                                            {/* Remove button */}
                                                            <Tooltip title={pm.type === 'link' ? 'Remove this Link account' : 'Remove this card'}>
                                                                <Button
                                                                    size="sm"
                                                                    variant="plain"
                                                                    color="neutral"
                                                                    loading={removingPaymentMethodId === pm.id}
                                                                    disabled={!!removingPaymentMethodId}
                                                                    onClick={() => handleRemovePaymentMethod(pm.id)}
                                                                    sx={{ minWidth: 'auto', px: 1 }}
                                                                >
                                                                    <DeleteOutline sx={{ fontSize: 18 }} />
                                                                </Button>
                                                            </Tooltip>
                                                        </Sheet>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Sheet
                                                    variant="soft"
                                                    color="neutral"
                                                    sx={{
                                                        p: 3,
                                                        borderRadius: 'md',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <CreditCard sx={{ fontSize: 32, color: 'neutral.400', mb: 1 }} />
                                                    <Typography level="title-sm" sx={{ mb: 0.5 }}>No saved payment methods</Typography>
                                                    <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                                                        Add a payment method by topping up your balance. A minimum £100 top-up is required.
                                                    </Typography>
                                                    <Button size="sm" variant="soft" color="primary" startDecorator={<Add />} onClick={() => setOpenBalanceTopUpModal(true)}>
                                                        Top up & add card
                                                    </Button>
                                                </Sheet>
                                            )}
                                        </Card>

                                        {/* ─── Section: Auto Recharge ─── */}
                                        <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 'lg' }}>
                                            {/* Header */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box sx={{
                                                        width: 40, height: 40, borderRadius: 'md',
                                                        bgcolor: franchise.recurring_leads_purchase ? 'success.softBg' : 'neutral.softBg',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <BoltOutlined sx={{ fontSize: 20, color: franchise.recurring_leads_purchase ? 'success.plainColor' : 'neutral.plainColor' }} />
                                                    </Box>
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography level="title-lg" sx={{ lineHeight: 1.2 }}>Auto Recharge</Typography>
                                                            <Chip
                                                                size="sm"
                                                                variant="soft"
                                                                color={franchise.recurring_leads_purchase ? 'success' : 'neutral'}
                                                                sx={{ '--Chip-minHeight': '22px' }}
                                                            >
                                                                {franchise.recurring_leads_purchase ? 'Active' : 'Off'}
                                                            </Chip>
                                                        </Box>
                                                        <Typography level="body-xs" color="neutral">
                                                            Automatically top up when your balance drops below a threshold
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {paymentMethods.length > 0 ? (
                                                <Box sx={{ mt: 2.5 }}>
                                                    {showAutoRechargeWarning && (
                                                        <Sheet
                                                            variant="outlined"
                                                            color="warning"
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: 'md',
                                                                mb: 2.5,
                                                                borderStyle: 'dashed',
                                                                borderWidth: 2,
                                                                bgcolor: autoRechargeLocked ? 'warning.softBg' : 'background.level1',
                                                            }}
                                                        >
                                                            <Typography level="title-sm" sx={{ mb: 0.5 }}>
                                                                {autoRechargeLocked
                                                                    ? 'Auto Recharge is off after 3 failed charges'
                                                                    : (failedAttempts > 0
                                                                        ? `Auto Recharge payment failed (${failedAttempts}/3)`
                                                                        : 'Auto Recharge warning')}
                                                            </Typography>
                                                            <Typography level="body-sm">
                                                                We could not charge your card.
                                                                We recommend topping up manually, then turning Auto Recharge back on.
                                                                This usually fixes cards that need 3DS authentication.
                                                            </Typography>
                                                            <Box sx={{ mt: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                                {manualPaymentUrl ? (
                                                                    <Button
                                                                        size="sm"
                                                                        color="warning"
                                                                        component="a"
                                                                        href={manualPaymentUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        Pay manually
                                                                    </Button>
                                                                ) : (
                                                                    <Button
                                                                        size="sm"
                                                                        color="warning"
                                                                        onClick={() => setOpenBalanceTopUpModal(true)}
                                                                    >
                                                                        Top up manually
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        </Sheet>
                                                    )}

                                                    {/* Form fields in responsive grid */}
                                                    <Box sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                                        gap: 2,
                                                        mb: 2.5,
                                                    }}>
                                                        <FormControl size="sm">
                                                            <FormLabel sx={{ fontWeight: 500 }}>When balance drops below</FormLabel>
                                                            <Input
                                                                type="number"
                                                                startDecorator="£"
                                                                value={autoRechargeThreshold}
                                                                onChange={(e) => setAutoRechargeThreshold(Number(e.target.value) || 0)}
                                                                slotProps={{ input: { min: 10, max: 1000, step: 1 } }}
                                                                sx={{ '--Input-focusedThickness': '2px' }}
                                                            />
                                                        </FormControl>
                                                        <FormControl size="sm">
                                                            <FormLabel sx={{ fontWeight: 500 }}>Bring balance up to</FormLabel>
                                                            <Input
                                                                type="number"
                                                                startDecorator="£"
                                                                value={autoRechargeAmount}
                                                                onChange={(e) => setAutoRechargeAmount(Number(e.target.value) || 0)}
                                                                slotProps={{ input: { min: 100, max: 5000, step: 1 } }}
                                                                sx={{ '--Input-focusedThickness': '2px' }}
                                                            />
                                                        </FormControl>
                                                    </Box>

                                                    <FormControl size="sm" sx={{ mb: 2.5 }}>
                                                        <FormLabel sx={{ fontWeight: 500 }}>Payment method</FormLabel>
                                                        <Select
                                                            value={selectedPaymentMethodId}
                                                            onChange={(_, v) => setSelectedPaymentMethodId(v)}
                                                            placeholder="Select card"
                                                            sx={{ '--Select-focusedThickness': '2px' }}
                                                        >
                                                            {paymentMethods.map((pm) => (
                                                                <Option key={pm.id} value={pm.id}>
                                                                    {pm.type === 'link' ? `Link (${pm.email || 'Link'})` : `•••• ${pm.last4} (${pm.brand})`}{pm.default ? " — default" : ""}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </FormControl>

                                                    {/* Actions */}
                                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                        {franchise.recurring_leads_purchase ? (
                                                            <>
                                                                <Button
                                                                    color="primary"
                                                                    loading={savingAutoRecharge}
                                                                    onClick={() => validateAndSaveAutoRecharge(true)}
                                                                    startDecorator={<CheckCircle sx={{ fontSize: 18 }} />}
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    Save settings
                                                                </Button>
                                                                <Button
                                                                    color="neutral"
                                                                    variant="outlined"
                                                                    loading={savingAutoRecharge}
                                                                    onClick={() => validateAndSaveAutoRecharge(false)}
                                                                >
                                                                    Disable
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Button
                                                                color="success"
                                                                disabled={autoRechargeLocked}
                                                                loading={savingAutoRecharge}
                                                                onClick={() => validateAndSaveAutoRecharge(true)}
                                                                startDecorator={<BoltOutlined sx={{ fontSize: 18 }} />}
                                                                sx={{ fontWeight: 600 }}
                                                            >
                                                                Enable Auto Recharge
                                                            </Button>
                                                        )}
                                                    </Box>
                                                    {autoRechargeLocked && (
                                                        <Typography level="body-xs" color="danger" sx={{ mt: 1 }}>
                                                            Complete a successful manual top-up first, then enable Auto Recharge again.
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Sheet
                                                    variant="soft"
                                                    color="neutral"
                                                    sx={{ mt: 2, p: 2.5, borderRadius: 'md', textAlign: 'center' }}
                                                >
                                                    <Typography level="body-sm" color="neutral">
                                                        <Link component="button" onClick={() => setOpenBalanceTopUpModal(true)} sx={{ fontWeight: 600 }}>
                                                            Add a payment method
                                                        </Link>{' '}to enable Auto Recharge.
                                                    </Typography>
                                                </Sheet>
                                            )}
                                        </Card>

                                        {/* ─── Section: Monthly Budget Cap ─── */}
                                        {paymentMethods.length > 0 && (
                                            <Card variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 'lg' }}>
                                                {/* Header */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                    <Box sx={{
                                                        width: 40, height: 40, borderRadius: 'md',
                                                        bgcolor: monthlyCapEnabled ? 'warning.softBg' : 'neutral.softBg',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <TuneOutlined sx={{ fontSize: 20, color: monthlyCapEnabled ? 'warning.plainColor' : 'neutral.plainColor' }} />
                                                    </Box>
                                                    <Box sx={{ mr: 1 }}>
                                                        <Typography level="title-lg" sx={{ lineHeight: 1.2 }}>Monthly Budget Cap</Typography>
                                                        <Typography level="body-xs" color="neutral">
                                                            Limit how much can be auto-recharged each calendar month
                                                        </Typography>
                                                    </Box>
                                                    <Switch
                                                        checked={monthlyCapEnabled}
                                                        onChange={(e) => setMonthlyCapEnabled(e.target.checked)}
                                                        slotProps={{
                                                            input: { 'aria-label': 'Toggle monthly cap' },
                                                        }}
                                                    />
                                                </Box>

                                                {monthlyCapEnabled && (
                                                    <Box>
                                                        {/* Budget gauge */}
                                                        <Sheet
                                                            variant="soft"
                                                            sx={{
                                                                p: { xs: 2, md: 2.5 },
                                                                borderRadius: 'md',
                                                                mb: 2,
                                                                bgcolor: capReached ? 'danger.50' : 'primary.50',
                                                                border: '1px solid',
                                                                borderColor: capReached ? 'danger.200' : 'primary.200',
                                                            }}
                                                        >
                                                            {/* Progress bar */}
                                                            <Box sx={{ mb: 1.5 }}>
                                                                <Box sx={{
                                                                    width: '100%', height: 10, borderRadius: 99,
                                                                    bgcolor: capReached ? 'danger.100' : 'primary.100',
                                                                    overflow: 'hidden',
                                                                }}>
                                                                    <Box sx={{
                                                                        height: '100%',
                                                                        width: `${capPercent}%`,
                                                                        borderRadius: 99,
                                                                        bgcolor: capReached ? 'danger.500' : capPercent > 75 ? 'warning.500' : 'primary.500',
                                                                        transition: 'width 0.5s ease, background-color 0.3s ease',
                                                                    }} />
                                                                </Box>
                                                            </Box>

                                                            {/* Amounts row */}
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                                                <Box>
                                                                    <Typography level="body-xs" color="neutral" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>
                                                                        Spent this month
                                                                    </Typography>
                                                                    <Typography level="title-lg" sx={{ color: capReached ? 'danger.700' : 'primary.700' }}>
                                                                        £{Number(monthlySpent).toFixed(2)}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ textAlign: 'right' }}>
                                                                    <Typography level="body-xs" color="neutral" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>
                                                                        Monthly limit
                                                                    </Typography>
                                                                    <Typography level="title-lg" color="neutral">
                                                                        £{Number(monthlyCapAmount).toFixed(2)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>

                                                            {/* Percentage label */}
                                                            <Typography level="body-xs" sx={{ mt: 1, color: capReached ? 'danger.600' : 'neutral.500' }}>
                                                                {capPercent.toFixed(0)}% of monthly budget used
                                                            </Typography>
                                                        </Sheet>

                                                        {/* Cap reached warning */}
                                                        {capReached && (() => {
                                                            const now = new Date();
                                                            const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                                                            const nextMonthName = nextMonth.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
                                                            return (
                                                                <Sheet
                                                                    variant="soft"
                                                                    color="warning"
                                                                    sx={{
                                                                        p: 2,
                                                                        borderRadius: 'md',
                                                                        mb: 2,
                                                                        display: 'flex',
                                                                        gap: 1.5,
                                                                        alignItems: 'flex-start',
                                                                    }}
                                                                >
                                                                    <WarningAmberRounded sx={{ color: 'warning.600', fontSize: 22, mt: 0.25 }} />
                                                                    <Box>
                                                                        <Typography level="title-sm" sx={{ color: 'warning.800', mb: 0.5 }}>
                                                                            Monthly cap reached
                                                                        </Typography>
                                                                        <Typography level="body-sm" sx={{ color: 'warning.700' }}>
                                                                            Auto-recharge is paused until <strong>{nextMonthName}</strong>.
                                                                            You can still top up manually, or increase your cap to resume sooner.
                                                                        </Typography>
                                                                    </Box>
                                                                </Sheet>
                                                            );
                                                        })()}

                                                        {/* Cap amount input */}
                                                        <FormControl size="sm" sx={{ maxWidth: { xs: '100%', sm: 280 } }}>
                                                            <FormLabel sx={{ fontWeight: 500 }}>Monthly cap amount</FormLabel>
                                                            <Input
                                                                type="number"
                                                                startDecorator="£"
                                                                value={monthlyCapAmount}
                                                                onChange={(e) => setMonthlyCapAmount(Number(e.target.value) || 0)}
                                                                slotProps={{ input: { min: 100, max: 50000, step: 50 } }}
                                                                sx={{ '--Input-focusedThickness': '2px' }}
                                                            />
                                                            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                                                Resets on the 1st of every month
                                                            </Typography>
                                                        </FormControl>
                                                    </Box>
                                                )}

                                                {!monthlyCapEnabled && (
                                                    <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                                                        No spending limit is set. Auto-recharge will top up without restriction.
                                                    </Typography>
                                                )}
                                            </Card>
                                        )}
                                    </Box>
                                    )}
                                </TabPanel>

                                {/* ────────────────────────────────────────────────
                                    TAB 1 — Campaigns
                                ──────────────────────────────────────────────── */}
                                <TabPanel value={1} sx={{ p: 0, pt: { xs: 2, md: 3 } }}>
                                    <Box>
                                        <Typography level="h3" sx={{ mb: 0.5 }}>Your Active Campaigns</Typography>
                                        <Typography level="body-sm" color="neutral" sx={{ mb: 2.5 }}>
                                            Click on any campaign to turn it on or off
                                        </Typography>

                                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(380px, 1fr))' } }}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    cursor: 'pointer',
                                                    borderRadius: 'lg',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        boxShadow: 'md',
                                                        transform: 'translateY(-2px)',
                                                    },
                                                }}
                                                onClick={() => toggleAd('roof_clean')}
                                            >
                                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                    <Box
                                                        sx={{
                                                            width: 52, height: 52, borderRadius: 'md',
                                                            bgcolor: franchise.roof_ads_status === 'Pending' ? 'warning.softBg' : franchise.roof_ads_status === 'Off' ? 'danger.softBg' : 'success.softBg',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: '22px',
                                                        }}
                                                    >
                                                        {franchise.roof_ads_status === 'Pending' ? '⏳' : franchise.roof_ads_status === 'Off' ? '⏸️' : '✅'}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                                            <Typography level="title-md">Roof Cleaning Ads</Typography>
                                                            <Chip
                                                                size="sm" variant="soft"
                                                                color={franchise.roof_ads_status === 'Pending' ? 'warning' : franchise.roof_ads_status === 'Off' ? 'danger' : 'success'}
                                                                sx={{ '--Chip-minHeight': '20px', fontSize: '0.65rem' }}
                                                            >
                                                                {franchise.roof_ads_status}
                                                            </Chip>
                                                        </Box>
                                                        <Typography level="body-xs" color="neutral">Multi-channel Advertising</Typography>
                                                    </Box>
                                                    <Typography level="body-sm" color="primary" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>Manage →</Typography>
                                                </Box>
                                            </Card>
                                        </Box>
                                    </Box>
                                </TabPanel>

                                {/* ────────────────────────────────────────────────
                                    TAB 2 — Past Charges
                                ──────────────────────────────────────────────── */}
                                <TabPanel value={2} sx={{ p: 0, pt: { xs: 2, md: 3 } }}>
                                    <Box>
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            justifyContent: 'space-between',
                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                            gap: 1.5,
                                            mb: 2.5,
                                        }}>
                                            <Box>
                                                <Typography level="h3" sx={{ mb: 0.25 }}>Past Charges & Top Ups</Typography>
                                                <Typography level="body-sm" color="neutral">
                                                    All your top-ups and advertising charges in one place
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                color="neutral"
                                                size="sm"
                                                startDecorator={<FileDownload sx={{ fontSize: 16 }} />}
                                                onClick={handleDownload}
                                            >
                                                Download CSV
                                            </Button>
                                        </Box>
                                        <PastPaymentsTable />
                                    </Box>
                                </TabPanel>

                                {/* ────────────────────────────────────────────────
                                    TAB 3 — Lead Conversions
                                ──────────────────────────────────────────────── */}
                                <TabPanel value={3} sx={{ p: 0, pt: { xs: 2, md: 3 } }}>
                                    <Box>
                                        <Typography level="h3" sx={{ mb: 0.25 }}>Lead Conversions</Typography>
                                        <Typography level="body-sm" color="neutral" sx={{ mb: 2.5 }}>
                                            All your lead conversion charges and refunds
                                        </Typography>
                                        <LeadConversionsTable />
                                    </Box>
                                </TabPanel>
                            </Tabs>
                        </>
                    )}
                </>
            )}
        </>
    );
}